import React, { useState, useEffect } from 'react';
import {
    SparklesIcon,
    SwatchIcon,
    UserGroupIcon,
    PresentationChartBarIcon,
    PhotoIcon,
    HeartIcon,
    ShoppingBagIcon,
    EyeIcon,
    PlusIcon,
    XMarkIcon,
    ArrowPathIcon,
    BookmarkIcon,
    ShareIcon,
    ChartBarIcon,
    Cog6ToothIcon,
    LightBulbIcon
} from '@heroicons/react/24/outline';
import { authorizedFetch } from '../utils/api';

const Styling = () => {
    const [activeTab, setActiveTab] = useState('create');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [renderTime, setRenderTime] = useState(null);

    // State for styling features
    const [outfits, setOutfits] = useState([]);
    const [lookbooks, setLookbooks] = useState([]);
    const [customerProfiles, setCustomerProfiles] = useState([]);
    const [stylingAnalytics, setStylingAnalytics] = useState({});
    const [selectedPieces, setSelectedPieces] = useState([]);
    const [currentOutfit, setCurrentOutfit] = useState(null);
    const [availablePieces, setAvailablePieces] = useState([]);

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        const startTime = performance.now();
        fetchStylingData().finally(() => {
            const endTime = performance.now();
            setRenderTime(Math.round(endTime - startTime));
        });
    }, []);

    const fetchStylingData = async () => {
        setLoading(true);
        try {
            const [outfitsResp, lookbooksResp, profilesResp, analyticsResp, piecesResp] = await Promise.all([
                authorizedFetch(`${backendUrl}/styling/outfits`),
                authorizedFetch(`${backendUrl}/styling/lookbooks`),
                authorizedFetch(`${backendUrl}/styling/customer-profiles`),
                authorizedFetch(`${backendUrl}/styling/analytics`),
                authorizedFetch(`${backendUrl}/styling/available-pieces`)
            ]);

            setOutfits(outfitsResp.data || []);
            setLookbooks(lookbooksResp.data || []);
            setCustomerProfiles(profilesResp.data || []);
            setStylingAnalytics(analyticsResp.data || {});
            setAvailablePieces(piecesResp.data || []);
        } catch (err) {
            console.error('Error fetching styling data:', err);
            setError('Failed to load styling data');
        } finally {
            setLoading(false);
        }
    };

    const generateOutfit = async (criteria) => {
        try {
            const response = await authorizedFetch(`${backendUrl}/styling/generate-outfit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(criteria)
            });

            if (response.outfit) {
                setCurrentOutfit(response.outfit);
                setSelectedPieces(response.outfit.pieces || []);
            }
        } catch (err) {
            console.error('Error generating outfit:', err);
        }
    };

    const saveOutfit = async (outfit) => {
        try {
            await authorizedFetch(`${backendUrl}/styling/save-outfit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(outfit)
            });

            fetchStylingData(); // Refresh data
        } catch (err) {
            console.error('Error saving outfit:', err);
        }
    };

    const LoadingSkeleton = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const CreateOutfitView = () => (
        <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <SparklesIcon className="h-8 w-8 text-purple-600 dark:text-purple-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">247</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Created Outfits</div>
                        </div>
                    </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <SwatchIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">1,284</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Available Pieces</div>
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <HeartIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">94.2%</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Customer Satisfaction</div>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <ShoppingBagIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">3.2x</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Cross-Sell Rate</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Outfit Generator */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <SparklesIcon className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                        AI-Powered Outfit Creator
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Create perfect combinations for your customers using advanced styling intelligence
                    </p>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Styling Criteria */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white">Styling Criteria</h4>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Occasion
                                </label>
                                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                    <option>Work/Professional</option>
                                    <option>Casual/Weekend</option>
                                    <option>Evening/Formal</option>
                                    <option>Special Events</option>
                                    <option>Travel</option>
                                    <option>Date Night</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Season
                                </label>
                                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                    <option>Spring/Summer 2024</option>
                                    <option>Fall/Winter 2024</option>
                                    <option>Transitional</option>
                                    <option>Year-Round</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Style Aesthetic
                                </label>
                                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                    <option>Minimalist Chic</option>
                                    <option>Bohemian Luxe</option>
                                    <option>Classic Elegance</option>
                                    <option>Modern Edge</option>
                                    <option>Romantic Feminine</option>
                                    <option>Street Style</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Budget Range
                                </label>
                                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                    <option>Luxury ($500+)</option>
                                    <option>Premium ($200-500)</option>
                                    <option>Mid-Range ($100-200)</option>
                                    <option>Accessible ($50-100)</option>
                                    <option>Mixed Investment</option>
                                </select>
                            </div>

                            <button
                                onClick={() => generateOutfit({
                                    occasion: 'Work/Professional',
                                    season: 'Spring/Summer 2024',
                                    style: 'Minimalist Chic',
                                    budget: 'Premium'
                                })}
                                className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center justify-center"
                            >
                                <SparklesIcon className="h-4 w-4 mr-2" />
                                Generate Outfit
                            </button>
                        </div>

                        {/* Current Outfit Preview */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white">Current Outfit</h4>

                            {currentOutfit ? (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg mb-4 flex items-center justify-center">
                                        <PhotoIcon className="h-12 w-12 text-gray-400" />
                                    </div>
                                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                                        {currentOutfit.name || 'Professional Chic Look'}
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">Pieces:</span>
                                            <span className="text-gray-900 dark:text-white">{selectedPieces.length}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">Total Value:</span>
                                            <span className="text-gray-900 dark:text-white">$1,247</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">Style Score:</span>
                                            <span className="text-green-600 dark:text-green-400">9.2/10</span>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 mt-4">
                                        <button
                                            onClick={() => saveOutfit(currentOutfit)}
                                            className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                                        >
                                            Save Outfit
                                        </button>
                                        <button className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm">
                                            <ShareIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                                    <SparklesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Generate an outfit to see the preview
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Styling Insights */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white">Styling Insights</h4>

                            <div className="space-y-3">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <LightBulbIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                            Color Harmony
                                        </span>
                                    </div>
                                    <p className="text-xs text-blue-800 dark:text-blue-200">
                                        Monochromatic palette creates sophisticated, elongating effect
                                    </p>
                                </div>

                                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <SwatchIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                                        <span className="text-sm font-medium text-green-900 dark:text-green-100">
                                            Texture Mix
                                        </span>
                                    </div>
                                    <p className="text-xs text-green-800 dark:text-green-200">
                                        Silk blouse + wool trousers creates luxurious contrast
                                    </p>
                                </div>

                                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <HeartIcon className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
                                        <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                                            Customer Appeal
                                        </span>
                                    </div>
                                    <p className="text-xs text-purple-800 dark:text-purple-200">
                                        92% of similar customers loved this combination
                                    </p>
                                </div>

                                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <ShoppingBagIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                                        <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                                            Investment Strategy
                                        </span>
                                    </div>
                                    <p className="text-xs text-yellow-800 dark:text-yellow-200">
                                        High-quality basics + statement accessories = versatile wardrobe
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Outfits */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Recent Styling Creations
                    </h3>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[
                            {
                                name: "Executive Power Look",
                                pieces: 4,
                                value: "$1,847",
                                rating: 9.4,
                                occasion: "Business",
                                image: "https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg"
                            },
                            {
                                name: "Weekend Luxe Casual",
                                pieces: 3,
                                value: "$892",
                                rating: 8.9,
                                occasion: "Casual",
                                image: "https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg"
                            },
                            {
                                name: "Evening Sophistication",
                                pieces: 5,
                                value: "$2,156",
                                rating: 9.7,
                                occasion: "Evening",
                                image: "https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg"
                            },
                            {
                                name: "Travel Chic Ensemble",
                                pieces: 4,
                                value: "$1,234",
                                rating: 8.6,
                                occasion: "Travel",
                                image: "https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg"
                            }
                        ].map((outfit, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg mb-3 overflow-hidden">
                                    <img
                                        src={outfit.image}
                                        alt={outfit.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="w-full h-full bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                                    </div>
                                </div>
                                <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">
                                    {outfit.name}
                                </h4>
                                <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Pieces:</span>
                                        <span className="text-gray-900 dark:text-white">{outfit.pieces}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Value:</span>
                                        <span className="text-gray-900 dark:text-white">{outfit.value}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Rating:</span>
                                        <span className="text-green-600 dark:text-green-400">{outfit.rating}/10</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full text-xs">
                                        {outfit.occasion}
                                    </span>
                                    <div className="flex space-x-1">
                                        <button className="p-1 text-gray-400 hover:text-blue-600">
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                        <button className="p-1 text-gray-400 hover:text-red-600">
                                            <HeartIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const LookbooksView = () => (
        <div className="space-y-6">
            {/* Lookbook Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">12</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Active Lookbooks</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">156</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Outfits</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">89%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Client Approval</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">$2.8M</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Value</div>
                </div>
            </div>

            {/* Lookbooks Grid */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Professional Lookbooks
                        </h3>
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center">
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Create Lookbook
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <BookmarkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Professional Lookbook Creation
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Create stunning lookbooks for different customer segments and occasions.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const CustomerProfilesView = () => (
        <div className="space-y-6">
            {/* Customer Profile Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">847</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Customer Profiles</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">23</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Style Personas</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">96.3%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Styling Accuracy</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">4.2x</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Repeat Purchase</div>
                </div>
            </div>

            {/* Customer Profiles */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Customer Style Profiles
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Customer Persona Styling
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Create and manage customer style profiles for personalized styling recommendations.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const AnalyticsView = () => (
        <div className="space-y-6">
            {/* Styling Analytics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Styling Performance Analytics
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Styling Success Analytics
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Track styling performance, customer satisfaction, and business impact.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const tabs = [
        { id: 'create', label: 'Create Outfits', icon: SparklesIcon },
        { id: 'lookbooks', label: 'Lookbooks', icon: BookmarkIcon },
        { id: 'profiles', label: 'Customer Profiles', icon: UserGroupIcon },
        { id: 'analytics', label: 'Styling Analytics', icon: ChartBarIcon }
    ];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        Professional Styling & Mix-and-Match
                    </h1>
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                </div>
                <LoadingSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                <button
                    onClick={fetchStylingData}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Professional Styling & Mix-and-Match
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Create perfect fashion combinations for your loyal customers using AI-powered styling intelligence.
                    Mix and match pieces to build complete looks that drive customer satisfaction and business success.
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                                        ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <Icon className="h-5 w-5 mr-2" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                <div className="p-6">
                    {activeTab === 'create' && <CreateOutfitView />}
                    {activeTab === 'lookbooks' && <LookbooksView />}
                    {activeTab === 'profiles' && <CustomerProfilesView />}
                    {activeTab === 'analytics' && <AnalyticsView />}
                </div>
            </div>

            {/* Performance Info */}
            {renderTime && (
                <div className="text-center text-xs text-gray-500 dark:text-gray-400">
                    Loaded in {renderTime}ms
                </div>
            )}
        </div>
    );
};

export default Styling;
